// The two images a video needs, side by side, each labelled with what it
// actually feeds.
//
// They were previously in two unrelated places with no indication that they
// serve different surfaces -- which is how videos ended up with a poster and
// no card crop, invisible on the very grid they were meant to appear in.

import React, { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageCropper from '../../components/ImageCropper';
import { useAdmin } from '../AdminStore';
import { describeError, patchVideo, uploadThumbnail } from '../api';

function dataURLToBlob(dataURL) {
  const [meta, payload] = dataURL.split(',');
  const mime = meta.split(':')[1].split(';')[0];
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export function ArtworkTab({ identifier, video, captureFrame }) {
  const { applyRecord, toast } = useAdmin();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const setPoster = async (blob, filename) => {
    setBusy(true);
    try {
      const url = await uploadThumbnail(identifier, blob, filename);
      applyRecord(identifier, await patchVideo(identifier, { thumbnailUrl: url }));
      setPreview(null);
      toast('Poster updated.', 'success');
    } catch (error) {
      toast(describeError(error, 'The poster was not changed.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const grabFrame = () => {
    const dataURL = captureFrame?.();
    if (!dataURL) {
      toast('Open the preview and start the video first — there is no frame to grab.', 'warning');
      return;
    }
    setPreview(dataURL);
  };

  return (
    <Box sx={{ display: 'flex', gap: '22px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <Box sx={{ flex: '1 1 320px', minWidth: 300 }}>
        <span className="admin-label">Poster · 16:9</span>
        <Typography sx={{ fontSize: 10.5, color: 'var(--ps-text-dim)', mb: '10px' }}>
          The player's poster frame and the featured hero image on the home page.
        </Typography>

        {video.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt="Poster"
            style={{
              width: '100%',
              maxWidth: 340,
              borderRadius: 'var(--ps-r-chip)',
              border: '1px solid var(--ps-line-subtle)',
              display: 'block',
              marginBottom: 10,
            }}
          />
        ) : (
          <Alert severity="warning" sx={{ mb: '10px' }}>No poster yet.</Alert>
        )}

        {preview && (
          <Box sx={{ mb: '10px' }}>
            <span className="admin-label">Captured frame</span>
            <img
              src={preview}
              alt="Captured frame"
              style={{
                width: '100%',
                maxWidth: 340,
                borderRadius: 'var(--ps-r-chip)',
                border: '1px solid var(--ps-gold)',
                display: 'block',
                margin: '4px 0 10px',
              }}
            />
            <Box sx={{ display: 'flex', gap: '4px' }}>
              <Button onClick={() => setPreview(null)}>Discard</Button>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => setPoster(dataURLToBlob(preview), 'frame.png')}
              >
                {busy ? 'Uploading…' : 'Use as poster'}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <Button component="label" disabled={busy}>
            Upload poster
            <input
              hidden
              type="file"
              accept="image/*"
              aria-label="Poster image"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setPoster(file, file.name);
              }}
            />
          </Button>
          <Button
            startIcon={<PhotoCameraIcon sx={{ fontSize: 15 }} />}
            onClick={grabFrame}
            disabled={busy}
          >
            Grab current frame
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: '1 1 320px', minWidth: 300 }}>
        <span className="admin-label">Card crop · 4:5</span>
        <Typography sx={{ fontSize: 10.5, color: 'var(--ps-text-dim)', mb: '10px' }}>
          What the home page grid renders. Without it a video shows no image there at all,
          however good its poster is.
        </Typography>

        {!video.minithumbnailUrl && !video.thumbnailUrl && (
          <Alert severity="info" sx={{ mb: '10px' }}>
            Set a poster first — the crop is taken from it unless you pick another file.
          </Alert>
        )}

        <ImageCropper
          video={video}
          identifier={identifier}
          onUpdateVideoData={(record) => applyRecord(identifier, record)}
          onNotify={toast}
        />
      </Box>
    </Box>
  );
}

export default ArtworkTab;
